import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Select,
  Avatar,
  App,
  Skeleton,
  Empty,
  Form,
  Modal,
  Input,
  InputNumber,
} from 'antd';
import { ArrowLeftOutlined, PictureOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getRoutineById,
  getRoutineDays,
  updateRoutineDayWarmUp,
  updateRoutineDayStretch,
  listRoutineDayExercises,
  normalizeRoutineDayExercisesResponse,
  createRoutineDayExercise,
  updateRoutineDayExercise,
  deleteRoutineDayExercise,
  createRoutineDayExerciseGroup,
  updateRoutineDayExerciseGroup,
  deleteRoutineDayExerciseGroup,
  listRoutineDayExerciseGroups,
} from '../../services/routines';
import { useAuth } from '../../hooks/useAuth';
import { listWarmUps } from '../../services/warmUps';
import { listStretches } from '../../services/stretches';
import { listExercises, getAvailableAlternatives } from '../../services/exercises';
import { resolveMedia } from '../../utils/media';
import RoutineDayExerciseModal from './RoutineDayExerciseModal';

const { Title, Text, Paragraph } = Typography;
const { useApp } = App;

const DEFAULT_OPTION_VALUE = 'none';

const RoutineDayDetailPage = () => {
  const { routineId, dayId } = useParams();
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [routine, setRoutine] = useState(null);
  const [routineDay, setRoutineDay] = useState(null);
  const [warmUps, setWarmUps] = useState([]);
  const [stretches, setStretches] = useState([]);
  /** Ejercicios sueltos del día (standalone_exercises); los de grupo vienen en exerciseGroups[].exercises */
  const [dayExercises, setDayExercises] = useState([]);
  const [exerciseCatalog, setExerciseCatalog] = useState([]);
  const [generalExerciseList, setGeneralExerciseList] = useState([]);
  const [updatingWarmUp, setUpdatingWarmUp] = useState(false);
  const [updatingStretch, setUpdatingStretch] = useState(false);
  const [exerciseModalVisible, setExerciseModalVisible] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [savingExercise, setSavingExercise] = useState(false);
  const [exerciseForm] = Form.useForm();

  const [exerciseGroups, setExerciseGroups] = useState([]);
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupForm] = Form.useForm();

  const [editGroupModalVisible, setEditGroupModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [savingEditGroup, setSavingEditGroup] = useState(false);
  const [editGroupForm] = Form.useForm();

  /** Grupos para el selector del modal de ejercicio (GET /exercise-groups) */
  const [modalExerciseGroupOptions, setModalExerciseGroupOptions] = useState([]);
  const [loadingModalGroupOptions, setLoadingModalGroupOptions] = useState(false);

  const normalizeItems = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.items)) return response.items;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const applyExercisesPayload = useCallback((raw) => {
    const { exerciseGroups: groups, standaloneExercises } = normalizeRoutineDayExercisesResponse(raw);
    setExerciseGroups(groups);
    setDayExercises(standaloneExercises);
  }, []);

  /** Sincroniza grupos + ejercicios sueltos con el servidor (tras crear grupo / ejercicio / borrar). */
  const refreshDayExercisesLayout = useCallback(async () => {
    if (!routineId || !dayId) return;
    try {
      const raw = await listRoutineDayExercises(routineId, dayId);
      applyExercisesPayload(raw);
    } catch (e) {
      console.error(e);
      message.error('No se pudo actualizar la lista de ejercicios');
    }
  }, [routineId, dayId, applyExercisesPayload, message]);

  const refreshExerciseGroupOptionsForModal = useCallback(async () => {
    if (!routineId || !dayId) return;
    setLoadingModalGroupOptions(true);
    try {
      const list = await listRoutineDayExerciseGroups(routineId, dayId);
      const arr = Array.isArray(list) ? list : normalizeItems(list);
      setModalExerciseGroupOptions(arr);
    } catch (e) {
      console.error(e);
      setModalExerciseGroupOptions((prev) => {
        if (prev.length) return prev;
        return exerciseGroups;
      });
    } finally {
      setLoadingModalGroupOptions(false);
    }
  }, [routineId, dayId, exerciseGroups]);

  const exerciseModalGroupSelectOptions = useMemo(
    () => [
      { value: DEFAULT_OPTION_VALUE, label: 'Sin grupo' },
      ...modalExerciseGroupOptions.map((g) => ({
        value: String(g.id),
        label: `${g.name || 'Grupo'} · pos. ${g.position ?? '-'}`,
      })),
    ],
    [modalExerciseGroupOptions]
  );

  useEffect(() => {
    if (!routineId || !dayId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routineId, dayId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        routineData,
        daysResponse,
        warmUpsResponse,
        stretchesResponse,
        exercisesResponse,
        generalExercisesResponse,
      ] = await Promise.all([
        getRoutineById(routineId),
        getRoutineDays(routineId),
        listWarmUps({ pageSize: 100 }),
        listStretches({ pageSize: 100 }),
        listExercises({ pageSize: 200 }),
        getAvailableAlternatives({ typeCode: 'GENERAL' }),
      ]);

      const dayItems = normalizeItems(daysResponse);
      const currentDay = dayItems.find((day) => String(day.id) === String(dayId));
      if (!currentDay) {
        message.error('Día de rutina no encontrado');
        navigate(`/routines/${routineId}/configure`);
        return;
      }

      let exercisesPayload = null;
      try {
        exercisesPayload = await listRoutineDayExercises(routineId, dayId);
      } catch {
        exercisesPayload = {
          exercise_groups: currentDay.exercise_groups || currentDay.exerciseGroups || [],
          standalone_exercises:
            currentDay.standalone_exercises ||
            currentDay.standaloneExercises ||
            currentDay.exercises ||
            [],
        };
      }

      setRoutine(routineData);
      setRoutineDay(currentDay);
      setWarmUps(normalizeItems(warmUpsResponse));
      setStretches(normalizeItems(stretchesResponse));
      applyExercisesPayload(exercisesPayload);
      setExerciseCatalog(normalizeItems(exercisesResponse));
      setGeneralExerciseList(generalExercisesResponse || []);
    } catch (error) {
      console.error(error);
      message.error('No se pudo cargar la información del día');
      navigate(`/routines/${routineId}/configure`);
    } finally {
      setLoading(false);
    }
  };

  const warmUpOptions = useMemo(
    () => [
      { value: DEFAULT_OPTION_VALUE, label: 'Sin calentamiento' },
      ...warmUps.map((item) => ({
        value: String(item.id),
        label: item.title || item.internal_title || `Calentamiento ${item.id}`,
      })),
    ],
    [warmUps]
  );

  const stretchOptions = useMemo(
    () => [
      { value: DEFAULT_OPTION_VALUE, label: 'Sin estiramiento' },
      ...stretches.map((item) => ({
        value: String(item.id),
        label: item.title || item.internal_title || `Estiramiento ${item.id}`,
      })),
    ],
    [stretches]
  );

  const exerciseLookup = useMemo(() => {
    const map = {};
    exerciseCatalog.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [exerciseCatalog]);

  const enrichedExercises = useMemo(
    () =>
      dayExercises
        .filter((exercise) => exercise.routine_day_group_id == null)
        .map((exercise) => ({
          ...exercise,
          exerciseData: exerciseLookup[exercise.exercise_id],
        }))
        .sort((a, b) => (a.position || 0) - (b.position || 0)),
    [dayExercises, exerciseLookup]
  );

  const enrichedGroups = useMemo(
    () =>
      exerciseGroups
        .map((group) => ({
          ...group,
          exercises: (group.exercises || []).map((exercise) => ({
            ...exercise,
            exerciseData: exerciseLookup[exercise.exercise_id],
          })),
        }))
        .sort((a, b) => (a.position || 0) - (b.position || 0)),
    [exerciseGroups, exerciseLookup]
  );

  /** Orden unificado: grupos y ejercicios sueltos por posición dentro del día */
  const dayItemsOrdered = useMemo(() => {
    const groupRows = enrichedGroups.map((g) => ({
      kind: 'group',
      position: g.position ?? 0,
      data: g,
    }));
    const exerciseRows = enrichedExercises.map((ex) => ({
      kind: 'exercise',
      position: ex.position ?? 0,
      data: ex,
    }));
    return [...groupRows, ...exerciseRows].sort((a, b) => a.position - b.position);
  }, [enrichedGroups, enrichedExercises]);

  const nextGroupPosition = useMemo(() => {
    const positions = [
      ...exerciseGroups.map((g) => g.position ?? 0),
      ...dayExercises.filter((e) => e.routine_day_group_id == null).map((e) => e.position ?? 0),
    ];
    const max = positions.length ? Math.max(...positions) : 0;
    return max + 1;
  }, [exerciseGroups, dayExercises]);

  const getCreatedBy = () => {
    if (!user) return '';
    return String(user.id ?? user.email ?? user.sub ?? '');
  };

  const exerciseOptions = useMemo(() => {
    return generalExerciseList.map((item) => ({
      value: String(item.id),
      label: item.title || item.internal_title || `Ejercicio ${item.id}`,
      type: item.executionType?.name || '',
      executionType: item.executionType,
    }));
  }, [generalExerciseList]);

  const getSelectValue = (id) => {
    if (id === null || id === undefined) {
      return DEFAULT_OPTION_VALUE;
    }
    return String(id);
  };

  const summaryImage = useMemo(() => {
    if (!routine) return null;
    return resolveMedia(routine.preview_path || routine.media_path);
  }, [routine]);

  const handleWarmUpChange = async (value) => {
    if (!routineDay) return;
    const warmUpId = value === DEFAULT_OPTION_VALUE ? null : Number(value);
    setUpdatingWarmUp(true);
    try {
      await updateRoutineDayWarmUp(routineId, dayId, warmUpId);
      setRoutineDay((prev) => ({
        ...prev,
        warm_up_id: warmUpId,
      }));
      message.success('Calentamiento actualizado');
    } catch (error) {
      console.error(error);
      message.error('No se pudo actualizar el calentamiento');
    } finally {
      setUpdatingWarmUp(false);
    }
  };

  const handleStretchChange = async (value) => {
    if (!routineDay) return;
    const stretchId = value === DEFAULT_OPTION_VALUE ? null : Number(value);
    setUpdatingStretch(true);
    try {
      await updateRoutineDayStretch(routineId, dayId, stretchId);
      setRoutineDay((prev) => ({
        ...prev,
        stretch_id: stretchId,
      }));
      message.success('Estiramiento actualizado');
    } catch (error) {
      console.error(error);
      message.error('No se pudo actualizar el estiramiento');
    } finally {
      setUpdatingStretch(false);
    }
  };

  const openExerciseModal = async (exercise = null) => {
    await refreshExerciseGroupOptionsForModal();

    const groupFieldValue =
      exercise?.routine_day_group_id != null && exercise?.routine_day_group_id !== ''
        ? String(exercise.routine_day_group_id)
        : DEFAULT_OPTION_VALUE;

    if (exercise) {
      exerciseForm.setFieldsValue({
        exercise_id: String(exercise.exercise_id),
        sets: exercise.sets || '',
        weight: exercise.weight || '',
        position: exercise.position ?? 1,
        extra_instructions: exercise.extra_instructions || '',
        routine_day_group_id: groupFieldValue,
      });
    } else {
      exerciseForm.resetFields();
      exerciseForm.setFieldsValue({
        position: nextGroupPosition,
        routine_day_group_id: DEFAULT_OPTION_VALUE,
      });
    }
    setEditingExercise(exercise);
    setExerciseModalVisible(true);
  };

  const closeExerciseModal = () => {
    setExerciseModalVisible(false);
    setEditingExercise(null);
    exerciseForm.resetFields();
  };

  const openGroupModal = () => {
    groupForm.resetFields();
    groupForm.setFieldsValue({ position: nextGroupPosition });
    setGroupModalVisible(true);
  };

  const closeGroupModal = () => {
    setGroupModalVisible(false);
    groupForm.resetFields();
  };

  const openEditGroupModal = (group) => {
    setEditingGroup(group);
    editGroupForm.setFieldsValue({
      name: group.name || '',
      position: group.position ?? 0,
    });
    setEditGroupModalVisible(true);
  };

  const closeEditGroupModal = () => {
    setEditGroupModalVisible(false);
    setEditingGroup(null);
    editGroupForm.resetFields();
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup?.id) return;
    try {
      const values = await editGroupForm.validateFields();
      const updatedBy = getCreatedBy().trim();
      if (!updatedBy) {
        message.warning('No se pudo identificar el usuario. Inicia sesión de nuevo.');
        return;
      }

      setSavingEditGroup(true);
      await updateRoutineDayExerciseGroup(routineId, dayId, editingGroup.id, {
        name: values.name.trim(),
        position: Number(values.position),
        updated_by: updatedBy,
      });

      message.success('Grupo actualizado');
      closeEditGroupModal();
      await refreshDayExercisesLayout();
      await refreshExerciseGroupOptionsForModal();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      const status = error?.__normalized?.status;
      const code = error?.__normalized?.code;
      const apiMsg =
        error?.__normalized?.message ||
        error?.response?.data?.error?.message ||
        'No se pudo actualizar el grupo';

      if (code === 'POSITION_IN_USE') {
        message.warning(apiMsg);
      } else if (status === 404) {
        message.error(apiMsg || 'Rutina, día o grupo no encontrado');
      } else if (status === 400) {
        message.error(apiMsg || 'Nombre o posición en conflicto');
      } else {
        message.error(apiMsg);
      }
    } finally {
      setSavingEditGroup(false);
    }
  };

  const handleDeleteGroup = (group) => {
    const label = group.name || `Grupo ${group.id}`;
    modal.confirm({
      title: '¿Eliminar grupo?',
      content: `Se eliminará "${label}". Verificar si tiene ejercicios asociados y muevelos o eliminalos antes de eliminar el grupo.`,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteRoutineDayExerciseGroup(routineId, dayId, group.id);
          message.success('Grupo eliminado');
          await refreshDayExercisesLayout();
          await refreshExerciseGroupOptionsForModal();
        } catch (error) {
          console.error(error);
          const msg = error?.__normalized?.message;
          message.error(msg || 'No se pudo eliminar el grupo');
        }
      },
    });
  };

  const handleCreateGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      const createdBy = getCreatedBy().trim();
      if (!createdBy) {
        message.warning('No se pudo identificar el usuario. Inicia sesión de nuevo.');
        return;
      }

      setSavingGroup(true);
      const payload = {
        name: values.name.trim(),
        position: Number(values.position),
        created_by: createdBy,
      };

      const created =
        (await createRoutineDayExerciseGroup(routineId, dayId, payload)) || null;
      if (!created) {
        message.error('Respuesta inválida al crear el grupo');
        return;
      }

      message.success('Grupo creado');
      closeGroupModal();
      await refreshDayExercisesLayout();
      await refreshExerciseGroupOptionsForModal();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      const status = error?.__normalized?.status;
      const code = error?.__normalized?.code;
      const apiMsg =
        error?.__normalized?.message ||
        error?.response?.data?.error?.message ||
        'No se pudo crear el grupo';

      if (code === 'POSITION_IN_USE') {
        message.warning(apiMsg);
      } else if (status === 404) {
        message.error(apiMsg || 'Rutina o día no encontrado');
      } else if (status === 400) {
        message.error(apiMsg || 'Nombre o posición en conflicto');
      } else {
        message.error(apiMsg);
      }
    } finally {
      setSavingGroup(false);
    }
  };

  const handleSaveExercise = async () => {
    try {
      const values = await exerciseForm.validateFields();
      const rawGroupId = values.routine_day_group_id;
      const routine_day_group_id =
        rawGroupId === DEFAULT_OPTION_VALUE || rawGroupId == null || rawGroupId === ''
          ? null
          : Number(rawGroupId);

      const payload = {
        exercise_id: Number(values.exercise_id),
        sets: values.sets?.trim() || null,
        position: Number(values.position),
        weight: values.weight?.trim() || null,
        extra_instructions: values.extra_instructions?.trim() || null,
        routine_day_group_id,
      };

      setSavingExercise(true);

      if (editingExercise) {
        await updateRoutineDayExercise(routineId, dayId, editingExercise.id, payload);
        message.success('Ejercicio actualizado');
      } else {
        await createRoutineDayExercise(routineId, dayId, payload);
        message.success('Ejercicio agregado');
      }

      closeExerciseModal();
      await refreshDayExercisesLayout();
    } catch (error) {
      if (error?.errorFields) return;
      console.error(error);
      const code = error?.__normalized?.code;
      const defaultMsg = error?.__normalized?.message || 'No se pudo guardar el ejercicio';

      if (code === 'POSITION_IN_USE') {
        message.warning(defaultMsg || 'La posición ya está registrada para este día');
      } else {
        message.error(defaultMsg);
      }
    } finally {
      setSavingExercise(false);
    }
  };

  const handleDeleteExercise = (exercise) => {
    const exerciseName = exerciseLookup[exercise.exercise_id]?.title || 'este ejercicio';
    modal.confirm({
      title: '¿Eliminar ejercicio?',
      content: exerciseName,
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteRoutineDayExercise(routineId, dayId, exercise.id);
          message.success('Ejercicio eliminado');
          await refreshDayExercisesLayout();
        } catch (error) {
          console.error(error);
          message.error('No se pudo eliminar el ejercicio');
        }
      },
    });
  };

  const renderSummary = () => (
    <Card
      style={{
        borderRadius: 16,
        backgroundColor: '#f7f7f7',
      }}
      bodyStyle={{ padding: 24 }}
    >
      <Row gutter={[24, 16]} align="middle">
        <Col xs={24} md={12}>
          <Space size={16}>
            {summaryImage ? (
              <img
                src={summaryImage}
                alt={routine?.title || 'Rutina'}
                style={{ width: 96, height: 96, borderRadius: 12, objectFit: 'cover' }}
              />
            ) : (
              <Avatar
                size={96}
                shape="square"
                icon={<PictureOutlined />}
                style={{ backgroundColor: '#e9e9e9', color: '#888' }}
              />
            )}
            <Space direction="vertical" size={4}>
              <Title level={4} style={{ margin: 0 }}>
                {routine?.title || 'Rutina'}
              </Title>
              <Text type="secondary">
                {`Semana ${routineDay?.week_number || '-'} | Día ${routineDay?.day_number || '-'} | ${routineDay?.title || 'Sin título'}`}
              </Text>
            </Space>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Text type="secondary" style={{ display: 'block' }}>
                Tipo Rutina
              </Text>
              <Text strong>{routine?.type?.name || routine?.type?.code || '-'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ display: 'block' }}>
                Enfoque
              </Text>
              <Text strong>{routine?.focus?.name || routine?.focus?.code || '-'}</Text>
            </Col>
          </Row>
          <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Text type="secondary" style={{ display: 'block' }}>
                Duración
              </Text>
              <Text strong>
                {routine?.duration?.days
                  ? `${routine.duration.days} día${routine.duration.days !== 1 ? 's' : ''}`
                  : '-'}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ display: 'block' }}>
                Ubicaciones
              </Text>
              <Text strong>
                {routine?.locations?.length
                  ? routine.locations.map((loc) => loc.name || loc.code).join(', ')
                  : '-'}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );

  const renderSelectors = () => (
    <Card
      style={{ borderRadius: 16 }}
      bodyStyle={{ padding: 24 }}
    >
      <Title level={5} style={{ marginTop: 0, color: '#8c8c8c' }}>
        Elige tu ejercicio
      </Title>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Text strong>Calentamiento *</Text>
          <Select
            showSearch
            placeholder="Elige calentamiento"
            style={{ width: '100%', marginTop: 8 }}
            value={getSelectValue(routineDay?.warm_up_id)}
            onChange={handleWarmUpChange}
            loading={updatingWarmUp}
            optionFilterProp="label"
            options={warmUpOptions}
          />
        </div>
        <div>
          <Text strong>Estiramiento *</Text>
          <Select
            showSearch
            placeholder="Elige estiramiento"
            style={{ width: '100%', marginTop: 8 }}
            value={getSelectValue(routineDay?.stretch_id)}
            onChange={handleStretchChange}
            loading={updatingStretch}
            optionFilterProp="label"
            options={stretchOptions}
          />
        </div>
      </Space>
    </Card>
  );

  const renderExercisesSection = () => (
    <Card
      style={{ borderRadius: 16 }}
      bodyStyle={{ padding: 24 }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            Ejercicios del día
          </Title>
        </Col>
        <Col>
          <Space wrap>
            <Button
              type="default"
              danger
              icon={<PlusOutlined />}
              onClick={openGroupModal}
            >
              Crear Grupo
            </Button>
            <Button
              type="primary"
              danger
              icon={<PlusOutlined />}
              onClick={() => {
                void openExerciseModal();
              }}
            >
              Agregar ejercicio
            </Button>
          </Space>
        </Col>
      </Row>
      {dayItemsOrdered.length === 0 ? (
        <Empty description="Aún no hay ejercicios ni grupos" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {dayItemsOrdered.map((item) => {
            if (item.kind === 'group') {
              const group = item.data;
              const groupMetaRed = { color: '#d32f2f', fontWeight: 600, fontSize: 13 };
              return (
                <div
                  key={`group-${group.id}`}
                  style={{
                    display: 'flex',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    overflow: 'hidden',
                    background: '#fff',
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      flexShrink: 0,
                      background: 'var(--sthenus-primary)',
                    }}
                    aria-hidden
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        background: '#f5f5f5',
                        padding: '12px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                        borderBottom: '1px solid #e8e8e8',
                      }}
                    >
                      <Text strong style={{ fontSize: 15, color: 'rgba(0,0,0,0.88)' }}>
                        Grupo: {group.name || 'Sin nombre'}
                      </Text>
                      <Space size="middle" wrap align="center">
                        <Text strong style={{ fontSize: 15, color: 'rgba(0,0,0,0.88)' }}>
                          Posición: {group.position ?? '-'}
                        </Text>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          aria-label="Editar grupo"
                          onClick={() => openEditGroupModal(group)}
                          style={{ color: '#8c8c8c' }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label="Eliminar grupo"
                          onClick={() => handleDeleteGroup(group)}
                        />
                      </Space>
                    </div>
                    <div style={{ padding: 16, background: '#fff' }}>
                      {(!group.exercises || group.exercises.length === 0) ? (
                        <Text type="secondary">Sin ejercicios en este grupo</Text>
                      ) : (
                        <Space direction="vertical" size={0} style={{ width: '100%' }}>
                          {group.exercises.map((exercise, exIdx) => {
                            const exerciseData = exercise.exerciseData || {};
                            const media = resolveMedia(exerciseData.preview_path || exerciseData.media_path);
                            const isLast = exIdx === group.exercises.length - 1;
                            return (
                              <div
                                key={exercise.id}
                                style={{
                                  paddingBottom: isLast ? 0 : 16,
                                  marginBottom: isLast ? 0 : 16,
                                  borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
                                }}
                              >
                                <Row gutter={[16, 12]} align="middle">
                                  <Col xs={24} sm={6} md={5}>
                                    {media ? (
                                      <img
                                        src={media}
                                        alt={exerciseData.title || 'Ejercicio'}
                                        style={{
                                          width: '100%',
                                          maxWidth: 120,
                                          aspectRatio: '4/3',
                                          borderRadius: 8,
                                          objectFit: 'cover',
                                          display: 'block',
                                        }}
                                      />
                                    ) : (
                                      <Avatar
                                        shape="square"
                                        size={96}
                                        icon={<PictureOutlined />}
                                        style={{ backgroundColor: '#f5f5f5', color: '#bfbfbf' }}
                                      />
                                    )}
                                  </Col>
                                  <Col xs={24} sm={12} md={14}>
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                      <Text strong style={{ fontSize: 15, display: 'block' }}>
                                        {exerciseData.title || 'Ejercicio'}
                                      </Text>
                                      <Text style={groupMetaRed}>
                                        Posición {exercise.position ?? '-'}
                                      </Text>
                                      <Text style={groupMetaRed}>
                                        Set: {exercise.sets || 'No especificado'}
                                      </Text>
                                      <Text style={groupMetaRed}>
                                        Peso: {exercise.weight || 'No especificado'}
                                      </Text>
                                      {exercise.extra_instructions ? (
                                        <Paragraph style={{ marginTop: 4, marginBottom: 0 }}>
                                          <Text strong>Instrucciones extra: </Text>
                                          {exercise.extra_instructions}
                                        </Paragraph>
                                      ) : null}
                                    </Space>
                                  </Col>
                                  <Col xs={24} sm={6} md={5} style={{ textAlign: 'right' }}>
                                    <Space>
                                      <Button
                                        type="text"
                                        icon={<EditOutlined />}
                                        style={{ color: '#8c8c8c' }}
                                        onClick={() => void openExerciseModal(exercise)}
                                      />
                                      <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDeleteExercise(exercise)}
                                      />
                                    </Space>
                                  </Col>
                                </Row>
                              </div>
                            );
                          })}
                        </Space>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            const exercise = item.data;
            const exerciseData = exercise.exerciseData || {};
            const media = resolveMedia(exerciseData.preview_path || exerciseData.media_path);
            const detailTextStyle = { color: '#b42318', fontWeight: 600, fontSize: 12 };

            return (
              <Card key={`exercise-${exercise.id}`} bodyStyle={{ padding: 16 }} style={{ borderRadius: 12 }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={5}>
                    {media ? (
                      <img
                        src={media}
                        alt={exerciseData.title || 'Ejercicio'}
                        style={{ width: '100%', borderRadius: 12, objectFit: 'cover' }}
                      />
                    ) : (
                      <Avatar
                        shape="square"
                        size={120}
                        icon={<PictureOutlined />}
                        style={{ backgroundColor: '#f0f0f0', color: '#8c8c8c' }}
                      />
                    )}
                  </Col>
                  <Col xs={24} md={17}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Title level={5} style={{ margin: 0 }}>
                        {exerciseData.title || 'Ejercicio'}
                      </Title>
                      <Text style={detailTextStyle}>Posición {exercise.position || '-'}</Text>
                      <Text style={detailTextStyle}>
                        Set: {exercise.sets || 'No especificado'}
                      </Text>
                      <Text style={detailTextStyle}>
                        Peso: {exercise.weight || 'No especificado'}
                      </Text>
                      {exercise.extra_instructions && (
                        <Paragraph style={{ marginTop: 4 }}>
                          <Text strong>Instrucciones extra: </Text>
                          {exercise.extra_instructions}
                        </Paragraph>
                      )}
                    </Space>
                  </Col>
                  <Col xs={24} md={2}>
                    <Space>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => void openExerciseModal(exercise)}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteExercise(exercise)}
                      />
                    </Space>
                  </Col>
                </Row>
              </Card>
            );
          })}
        </Space>
      )}
    </Card>
  );

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Card style={{ borderRadius: 16 }}>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(`/routines/${routineId}/configure`)}
              />
              <Title level={3} style={{ margin: 0 }}>
                Editar día de rutina
              </Title>
            </Space>
          </Col>
        </Row>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {renderSummary()}
          {renderSelectors()}
          {renderExercisesSection()}
        </Space>
      </Card>

      <RoutineDayExerciseModal
        visible={exerciseModalVisible}
        onCancel={closeExerciseModal}
        onSubmit={handleSaveExercise}
        form={exerciseForm}
        exerciseOptions={exerciseOptions}
        groupOptions={exerciseModalGroupSelectOptions}
        loadingGroups={loadingModalGroupOptions}
        loading={savingExercise}
        editingExercise={editingExercise}
      />

      <Modal
        title="Crear grupo de ejercicios"
        open={groupModalVisible}
        onCancel={closeGroupModal}
        destroyOnClose
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={closeGroupModal}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={savingGroup}
            onClick={() => {
              void handleCreateGroup();
            }}
          >
            Agregar grupo
          </Button>,
        ]}
      >
        <Form form={groupForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nombre del grupo"
            rules={[
              { required: true, message: 'Ingresa el nombre del grupo' },
              { whitespace: true, message: 'El nombre no puede estar vacío' },
              {
                pattern: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s._-]+$/,
                message: 'Solo caracteres alfanuméricos (y espacio, guión o punto)',
              },
              { max: 120, message: 'Máximo 120 caracteres' },
            ]}
          >
            <Input placeholder="Ej. Tren superior" allowClear />
          </Form.Item>
          <Form.Item
            name="position"
            label="Posición"
            rules={[
              { required: true, message: 'Ingresa la posición' },
              {
                type: 'number',
                min: 0,
                max: 9999,
                message: 'Entre 0 y 9999',
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0} max={9999} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Editar grupo de ejercicios"
        open={editGroupModalVisible}
        onCancel={closeEditGroupModal}
        destroyOnClose
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={closeEditGroupModal}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            loading={savingEditGroup}
            onClick={() => {
              void handleUpdateGroup();
            }}
          >
            Guardar cambios
          </Button>,
        ]}
      >
        <Form form={editGroupForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nombre del grupo"
            rules={[
              { required: true, message: 'Ingresa el nombre del grupo' },
              { whitespace: true, message: 'El nombre no puede estar vacío' },
              {
                pattern: /^[a-zA-Z0-9áéíóúÁÉÍÓÚñÑüÜ\s._-]+$/,
                message: 'Solo caracteres alfanuméricos (y espacio, guión o punto)',
              },
              { max: 120, message: 'Máximo 120 caracteres' },
            ]}
          >
            <Input placeholder="Ej. Tren superior" allowClear />
          </Form.Item>
          <Form.Item
            name="position"
            label="Posición"
            rules={[
              { required: true, message: 'Ingresa la posición' },
              {
                type: 'number',
                min: 0,
                max: 9999,
                message: 'Entre 0 y 9999',
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0} max={9999} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RoutineDayDetailPage;


